import React, { useState, useEffect, useRef } from "react";
import "./LeaveManagement.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
    FaRegEye,
    FaRegStickyNote,
    FaEdit,
    FaCheck,
    FaTimes,
    FaChevronLeft,
    FaChevronRight,
    FaPlus,
    FaCalendarAlt,
    FaSignOutAlt,
} from "react-icons/fa";
import { GiVacuumCleaner, GiSandsOfTime } from "react-icons/gi";
import { RiLeafLine } from "react-icons/ri";

const LeaveManagement = () => {
    const [leaveData, setLeaveData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hoveredCell, setHoveredCell] = useState(null);
    const [clickedCell, setClickedCell] = useState(null);
    const [academicYear, setAcademicYear] = useState(null);
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [selectedAbsence, setSelectedAbsence] = useState(null);
    const [newNotes, setNewNotes] = useState("");
    const [editMode, setEditMode] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const [showWelcome, setShowWelcome] = useState(true);
    const tooltipRef = useRef(null);

    const months = [
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
    ];

    const monthToNumber = {
        Jan: 1,
        Feb: 2,
        Mar: 3,
        Apr: 4,
        May: 5,
        Jun: 6,
        Jul: 7,
        Aug: 8,
        Sep: 9,
        Oct: 10,
        Nov: 11,
        Dec: 12,
    };

    const userId = localStorage.getItem("userId");
    const userName = localStorage.getItem("userName");

    const fixDocumentUrl = (url) => {
        if (!url) return "";
        if (url.startsWith("/")) {
            return `https://backend.ems.hiradentallab.com${url}`;
        }
        const urlObj = new URL(url, "https://backend.ems.hiradentallab.com");
        urlObj.port = "5544";
        return urlObj.toString();
    };

    useEffect(() => {
        const determineAcademicYear = () => {
            const now = new Date();
            const currentMonth = now.getMonth() + 1; // 1-12
            const currentYear = now.getFullYear();
            return currentMonth >= 6 ? currentYear : currentYear - 1;
        };

        setAcademicYear(determineAcademicYear());
    }, []);

    useEffect(() => {
        const fetchLeaveRecords = async () => {
            try {
                if (!userId || !academicYear) return;

                setLoading(true);
                setError(null);

                const response = await axios.get(
                    `https://backend.ems.hiradentallab.com/api/attendance/user/${userId}`,
                    {
                        params: { academicYear: `${academicYear}-${academicYear + 1}` },
                        headers: {
                            "Content-Type": "application/json",
                        },
                        withCredentials: false,
                    }
                );

                if (response.data) {
                    const record = response.data;
                    const monthlyData = {};

                    // ✅ FIXED: Correct year assignment based on month number
                    months.forEach((monthName) => {
                        const monthNum = monthToNumber[monthName];
                        // June to December → academicYear (e.g., 2025)
                        // January to May → academicYear + 1 (e.g., 2026)
                        const year = monthNum >= 6 ? academicYear : academicYear + 1;

                        const monthData = record.monthlyData?.find(
                            (m) => m.month === monthNum && m.year === year
                        );

                        monthlyData[monthName] = {
                            totalAbsences: monthData?.totalAbsences || 0,
                            details:
                                monthData?.absences?.map((absence) => ({
                                    ...absence,
                                    date: new Date(absence.date).toISOString().split("T")[0],
                                    notes: absence.notes || "",
                                })) || [],
                        };
                    });

                    const processedData = {
                        ...record,
                        monthlyData,
                        holidayAllowance: record.yearlyLeaveCount || "Not Set",
                        name: record.name || userName || "Unknown User",
                        userId: userId,
                    };

                    setLeaveData(processedData);
                } else {
                    throw new Error("No data received from server");
                }
            } catch (error) {
                console.error("Error fetching leave records:", error);
                setError(error.response?.data?.message || error.message);
                setLeaveData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchLeaveRecords();
    }, [academicYear, userId, userName]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
                const isAbsenceCell = event.target.closest(".has-absences");
                if (!isAbsenceCell) {
                    setClickedCell(null);
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (successMessage || error) {
            const timer = setTimeout(() => {
                setSuccessMessage(null);
                setError(null);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [successMessage, error]);

    const handleNotesClick = (absence, month) => {
        setSelectedAbsence({ ...absence, month });
        const userContent = absence.notes ? absence.notes.split("\n\n[")[0] : "";
        setNewNotes(userContent);
        setShowNotesModal(true);
        setEditMode(false);
        setError(null);
    };

    const handleSaveNotes = async () => {
        if (!selectedAbsence) return;

        setIsSubmitting(true);
        setError(null);

        try {
            const monthNum = monthToNumber[selectedAbsence.month];
            if (!monthNum) {
                throw new Error("Invalid month name provided");
            }

            const formattedDate = selectedAbsence.date.includes("T")
                ? selectedAbsence.date.split("T")[0]
                : selectedAbsence.date;

            const now = new Date();
            const timestamp = `\n\n[Updated on ${now.toLocaleString()}]`;
            const notesWithTimestamp = newNotes.trim() + timestamp;

            const payload = {
                userId: userId,
                academicYear: `${academicYear}-${academicYear + 1}`,
                month: monthNum,
                date: formattedDate,
                notes: notesWithTimestamp,
            };

            const response = await axios.put(
                "https://backend.ems.hiradentallab.com/api/attendance/absences/notes",
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: false,
                }
            );

            if (response.data.success) {
                setLeaveData((prev) => {
                    const updatedMonthlyData = { ...prev.monthlyData };
                    const monthKey = selectedAbsence.month;

                    updatedMonthlyData[monthKey] = {
                        ...updatedMonthlyData[monthKey],
                        details: updatedMonthlyData[monthKey].details.map((absence) => {
                            if (absence.date === formattedDate) {
                                return {
                                    ...absence,
                                    notes: notesWithTimestamp,
                                };
                            }
                            return absence;
                        }),
                    };

                    return {
                        ...prev,
                        monthlyData: updatedMonthlyData,
                    };
                });

                setSuccessMessage(`Notes updated for ${formattedDate}`);
                setShowNotesModal(false);
            } else {
                throw new Error(response.data.message || "Failed to update notes");
            }
        } catch (error) {
            console.error("Error saving notes:", error);
            setError(error.response?.data?.message || error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancelNotes = () => {
        setShowNotesModal(false);
        setSelectedAbsence(null);
        setNewNotes("");
        setEditMode(false);
        setError(null);
    };

    const handleCellHover = (event, month, details) => {
        if (details.length > 0) {
            const tooltipWidth = 300;
            const viewportWidth = window.innerWidth;
            let leftPosition = event.clientX + 20;
            if (leftPosition + tooltipWidth > viewportWidth) {
                leftPosition = event.clientX - tooltipWidth - 20;
            }
            const tooltipHeight = 200;
            const viewportHeight = window.innerHeight;
            let topPosition = event.clientY - tooltipHeight - 10;
            if (topPosition < 0) topPosition = event.clientY + 10;
            setHoveredCell({
                month,
                details,
                clientX: leftPosition,
                clientY: topPosition,
            });
        }
    };

    const handleCellClick = (event, month, details) => {
        if (details.length > 0) {
            const tooltipWidth = 300;
            const viewportWidth = window.innerWidth;
            let leftPosition = event.clientX + 20;
            if (leftPosition + tooltipWidth > viewportWidth) {
                leftPosition = event.clientX - tooltipWidth - 20;
            }
            const tooltipHeight = 200;
            const viewportHeight = window.innerHeight;
            let topPosition = event.clientY - tooltipHeight - 10;
            if (topPosition < 0) topPosition = event.clientY + 10;
            const newClickedCell = {
                month,
                details,
                clientX: leftPosition,
                clientY: topPosition,
            };
            if (clickedCell && clickedCell.month === month) {
                setClickedCell(null);
            } else {
                setClickedCell(newClickedCell);
            }
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toISOString().split("T")[0];
    };

    const handlePrevYear = () => {
        setAcademicYear((prev) => prev - 1);
        setClickedCell(null);
    };

    const handleNextYear = () => {
        setAcademicYear((prev) => prev + 1);
        setClickedCell(null);
    };

    const toggleEditMode = () => {
        setEditMode(!editMode);
    };

    const closeWelcome = () => {
        setShowWelcome(false);
    };

    const handleLogout = () => {
        localStorage.removeItem("userId");
        localStorage.removeItem("userName");
        window.location.href = "/login";
    };

    return (
        <div className="leave-management">
            {/* Page Title */}
            <div className="page-title">
                <h1>Leave Management Dashboard</h1>
                <p>Track and manage your leave records throughout the academic year</p>
            </div>

            {/* Animated Welcome Message */}
            <AnimatePresence>
                {showWelcome && (
                    <motion.div
                        className="welcome-popup"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ type: "spring", damping: 20, stiffness: 300 }}
                    >
                        <div className="welcome-character">
                            <motion.div
                                animate={{
                                    y: [0, -8, 0],
                                }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    repeatType: "reverse",
                                    ease: "easeInOut",
                                }}
                            >
                                <RiLeafLine className="leaf-icon" />
                            </motion.div>
                        </div>
                        <div className="welcome-content">
                            <h3>Welcome to Your Leave Dashboard!</h3>
                            <p>
                                Manage your time off with ease. Track leaves and stay organized.
                            </p>
                            <button onClick={closeWelcome} className="welcome-close-btn">
                                Get Started
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="header-section">
                <div className="user-header">
                    <div className="welcome-message">
                        <span className="welcome-text">Welcome,</span>
                    </div>
                    <h1 className="user-name-display">
                        {leaveData?.name || "Loading..."}
                    </h1>
                </div>

                <div className="leave-summary">
                    <div className="date-navigation">
                        <button onClick={handlePrevYear} className="nav-btn">
                            <FaChevronLeft />
                        </button>
                        <span className="academic-year">{`${academicYear} - ${
                            academicYear + 1
                        }`}</span>
                        <button onClick={handleNextYear} className="nav-btn">
                            <FaChevronRight />
                        </button>
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {(successMessage || error) && (
                    <motion.div
                        className={successMessage ? "success-message" : "error-message"}
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        {successMessage || error}
                    </motion.div>
                )}
            </AnimatePresence>

            {loading ? (
                <div className="loading">
                    <div className="spinner"></div>
                    Loading your leave records...
                </div>
            ) : leaveData ? (
                <div className="table-container">
                    <table className="allowance-table">
                        <thead>
                            <tr>
                                <th>Month</th>
                                <th>Absences</th>
                            </tr>
                        </thead>
                        <tbody>
                            {months.map((month) => (
                                <tr key={month}>
                                    <td>{month}</td>
                                    <td
                                        className={
                                            leaveData.monthlyData[month]?.totalAbsences > 0
                                                ? "has-absences"
                                                : ""
                                        }
                                        onMouseEnter={(e) =>
                                            handleCellHover(
                                                e,
                                                month,
                                                leaveData.monthlyData[month]?.details || []
                                            )
                                        }
                                        onMouseLeave={() => setHoveredCell(null)}
                                        onClick={(e) =>
                                            handleCellClick(
                                                e,
                                                month,
                                                leaveData.monthlyData[month]?.details || []
                                            )
                                        }
                                    >
                                        {leaveData.monthlyData[month]?.totalAbsences || 0}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="no-data">
                    {error ? error : "No leave records found for this user"}
                </div>
            )}

            {/* Tooltip & Modal code remains exactly the same */}
            <AnimatePresence>
                {(hoveredCell || clickedCell) &&
                    (hoveredCell?.details?.length > 0 ||
                        clickedCell?.details?.length > 0) && (
                        <motion.div
                            ref={tooltipRef}
                            className="absence-tooltip"
                            style={{
                                top: (clickedCell || hoveredCell).clientY,
                                left: (clickedCell || hoveredCell).clientX,
                                pointerEvents: clickedCell ? "auto" : "none",
                            }}
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                        >
                            <div className="tooltip-header">
                                <h4>Absence Details</h4>
                                <div className="tooltip-subheader">
                                    <FaCalendarAlt /> {(clickedCell || hoveredCell).month} -{" "}
                                    {(clickedCell || hoveredCell).details.length} day
                                    {(clickedCell || hoveredCell).details.length !== 1 ? "s" : ""}
                                </div>
                            </div>
                            <div className="tooltip-content">
                                {(clickedCell || hoveredCell).details.map((absence, index) => (
                                    <motion.div
                                        key={index}
                                        className="absence-detail"
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                    >
                                        <div className="absence-date">
                                            <FaCalendarAlt /> {formatDate(absence.date)}
                                        </div>
                                        <div className="absence-actions">
                                            {absence.document && (
                                                <a
                                                    href={fixDocumentUrl(absence.document)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="document-link"
                                                    title="View Document"
                                                    onClick={(e) => {
                                                        if (
                                                            !absence.document ||
                                                            absence.document.trim() === ""
                                                        ) {
                                                            e.preventDefault();
                                                            alert("Document not available");
                                                        }
                                                    }}
                                                >
                                                    <FaRegEye />
                                                </a>
                                            )}
                                            <button
                                                className="notes-btn"
                                                onClick={() =>
                                                    handleNotesClick(
                                                        absence,
                                                        (clickedCell || hoveredCell).month
                                                    )
                                                }
                                                title={absence.notes ? "View/Edit Notes" : "Add Notes"}
                                            >
                                                <FaRegStickyNote />
                                            </button>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                            {clickedCell && (
                                <button
                                    className="close-tooltip-btn"
                                    onClick={() => setClickedCell(null)}
                                >
                                    ×
                                </button>
                            )}
                        </motion.div>
                    )}
            </AnimatePresence>

            <AnimatePresence>
                {showNotesModal && selectedAbsence && (
                    <div className="modal-backdrop">
                        <motion.div
                            className="notes-modal"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ type: "spring", damping: 20 }}
                        >
                            <div className="modal-header">
                                <h3>
                                    <FaRegStickyNote className="notes-icon" />
                                    Notes for {formatDate(selectedAbsence.date)}
                                </h3>
                                <div className="modal-actions">
                                    {!editMode ? (
                                        <button
                                            className="action-btn edit-btn"
                                            onClick={toggleEditMode}
                                            title="Edit Notes"
                                        >
                                            <FaEdit />
                                        </button>
                                    ) : (
                                        <>
                                            <button
                                                className="action-btn save-btn"
                                                onClick={handleSaveNotes}
                                                disabled={isSubmitting}
                                                title="Save"
                                            >
                                                <FaCheck />
                                            </button>
                                            <button
                                                className="action-btn cancel-btn"
                                                onClick={toggleEditMode}
                                                title="Cancel"
                                            >
                                                <FaTimes />
                                            </button>
                                        </>
                                    )}
                                    <button
                                        className="action-btn close-btn"
                                        onClick={handleCancelNotes}
                                        disabled={isSubmitting}
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>

                            <div className="modal-content">
                                {editMode ? (
                                    <div className="notes-editor">
                                        <textarea
                                            value={newNotes}
                                            onChange={(e) => setNewNotes(e.target.value)}
                                            className="notes-input"
                                            placeholder="Type your notes here..."
                                            rows={6}
                                            autoFocus
                                        />
                                    </div>
                                ) : selectedAbsence.notes ? (
                                    <div className="notes-preview">
                                        {selectedAbsence.notes.split("\n\n").map((part, index) => (
                                            <p
                                                key={index}
                                                className={part.startsWith("[") ? "note-timestamp" : ""}
                                            >
                                                {part}
                                            </p>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="empty-notes">
                                        <FaRegStickyNote className="empty-icon" />
                                        <p>No notes added yet for this absence</p>
                                        <button className="add-notes-btn" onClick={toggleEditMode}>
                                            <FaPlus /> Add Notes
                                        </button>
                                    </div>
                                )}
                            </div>

                            {error && (
                                <motion.div
                                    className="error-message"
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    exit={{ opacity: 0, height: 0 }}
                                >
                                    {error}
                                </motion.div>
                            )}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default LeaveManagement;